/**
 * friday 请求器青春版
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosServiceApi, redirectLogin } from 'src/api/base';
import { object2Query } from '@code/asker';
import { debounce, get, isArray, isEmpty, isEqual, isObject, isString } from 'lodash';
import { getArray, getNumber } from 'utils';
import { PaginationProps } from 'antd/lib/pagination';
import { useMergeRender } from 'src/hooks';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import history from 'src/helpers/history';

const errorNotice = debounce((text, response?) => {
    message.error(text);
}, 500);


const isApiTimeout = (error): boolean => {
    return error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1;
};

const isNetworkError = (error): boolean => {
    return error.message === 'Network Error';
};

const defaultHooks = {
    requestSuccessHook: (config: AxiosRequestConfig) => {
        return config;
    },
    requestErrorHooks: (error) => {
        return Promise.reject(error);
    },
    responseSuccessHooks: (response) => {
        return response.data;
    },
    responseErrorHooks: (error) => {
        const status = get(error, 'response.status');
        // 超时、网络异常
        if (isApiTimeout(error)) {
            errorNotice('接口请求超时');
        }

        if (isNetworkError(error)) {
            errorNotice('网络异常');
        }

        if (error.response && status === 401) {
            message.info('登录超时');
            redirectLogin(window.location.pathname);
            return Promise.reject(error);
        }
        if ([502, 503].indexOf(status) > -1) {
            message.error('接口错误');
            history.push('/503');
            return Promise.reject(error);
        }
        if ([402].indexOf(status) !== -1) {
            message.error('接口错误');
            history.push('/402');
            return Promise.reject(error);
        }
        // 系统错误
        if (error.response && status >= 400) {
            const { data } = error.response;
            // blob to  json
            if (Object.prototype.toString.call(data) === "[object Blob]" && (data.type === 'application/json')) {
                let reader = new FileReader() as any;
                reader.readAsText(data, 'utf-8');
                reader.onload = () => {
                    const text = JSON.parse(reader.result);
                    message.error(text.message);
                };
            } else {
                const notice = get(error, 'response.data.message') || '系统异常';
                errorNotice(notice, error.response);
            }
        }
        return Promise.reject(error);
    }
};


interface AxiosHooks {
    requestSuccessHook?: (config: AxiosRequestConfig) => AxiosRequestConfig;
    requestErrorHooks?: (error: any) => Promise<any>;
    responseSuccessHooks?: (response: AxiosResponse<any>) => AxiosResponse<any>;
    responseErrorHooks?: (error: any) => Promise<any>;
}

export const httpAxios = (options: AxiosRequestConfig = {}, hooks: AxiosHooks = {}) => {
    const instance = axios.create(options);

    instance.interceptors.request.use(config => {
        // get 请求无法加上content-type解决方法
        if (config.method === 'get') {
            config.data = { unused: 0 };
        }
        if (!hooks.requestSuccessHook) {
            return defaultHooks.requestSuccessHook(config);
        }
        return hooks.requestSuccessHook!(config);
    }, error => {
        if (!hooks.requestErrorHooks) {
            return defaultHooks.requestErrorHooks(error);
        }
        return hooks.requestErrorHooks(error);
    });

    instance.interceptors.response.use(function (response) {
        if (!hooks.responseSuccessHooks) {
            return defaultHooks.responseSuccessHooks(response);
        }
        return hooks.responseSuccessHooks(response);
    }, function (error) {
        if (!hooks.responseErrorHooks) {
            return defaultHooks.responseErrorHooks(error);
        }
        return hooks.responseErrorHooks(error);
    });
    return instance;
};


interface ICacheMapValue {
    [x: string]: {
        response: any;
        lastFetchTime: number;
    };
};

type CacheDurationTime = number | 'infinity';

let cacheMap: ICacheMapValue = {};

interface CacheApiConfig extends AxiosRequestConfig {
    cacheDurationTime?: CacheDurationTime;
    responseSuccess?: (response: any) => any;
}


interface GetReturnType<T = any, K = any> extends CacheApiConfig {
    type: 'get';
    payload?: T;
}

export const createGetApi = <Payload = any, Data = any>(congig: CacheApiConfig) => {
    return (payload?: Payload) => {
        return (): GetReturnType<Payload, Data> => ({
            type: 'get',
            payload,
            ...congig
        });
    };
};


interface PostReturnType<T = any, K = any> extends CacheApiConfig {
    type: 'post';
    payload?: T;
}

export const createPostApi = <Payload = any, Data = any>(congig: CacheApiConfig) => {
    return (payload?: Payload) => {
        return (): PostReturnType<Payload, Data> => ({
            type: 'post',
            payload,
            ...congig
        });
    };
};


export type ApiConfig<T = any, K = any> = () => GetReturnType<T, K> | PostReturnType<T, K>;


interface Options {
    // 开启分页
    paginated?: boolean;
    // 分页参数放到body里（仅 post 接口生效）
    pageInBody?: boolean;
    // 分页额外参数
    paginationProps?: PaginationProps;
    // 对请求进行缓存
    useCache?: boolean;
    // 使用 isValidating
    useValidating?: boolean;
    // 超时
    timeout?: number;
    // 超时回调
    onLoadingSlow?: () => void;
    // 请求失败回调
    onError?: (error) => void;
};

const useKey = (url: string, payload: any) => {
    const key = `${url}?${JSON.stringify(payload)}`;
    const keyRef = useRef(key);

    if (keyRef.current !== key) {
        keyRef.current = key;
    }
    return keyRef.current;
};

const defaultState = {
    dataJson: {} as any,
    dataArray: [] as any[],
    data: {} as any,
    response: {} as any
};


interface ReturnType<Data = any> {
    isValidating: boolean;
    revalidate: () => void;
    pagination: PaginationProps;
    data: any;
    dataArray: Data[];
    dataJson: Data;
    response: any;
    [x: string]: any;
};

export const useRequest = <T = any, K = any>(apiConfig: ApiConfig<T, K> | null, options: Options = {}): ReturnType<K> => {
    const {
        paginated = false,
        pageInBody = false,
        paginationProps = {},
        useValidating = true,
        useCache = true,
        timeout = 20000,
        onLoadingSlow,
        onError
    } = options;

    const { current, pageSize, onChange, onShowSizeChange, ...otherPageProps } = paginationProps;

    const paginationRef = useRef({
        pageNum: current || 1,
        pageSize: pageSize || 10,
    });

    const {
        type,
        url = '',
        payload,
        cacheDurationTime,
        responseSuccess,
        ...otherConfig
    } = apiConfig?.() || {};

    const payloadRef = useRef(payload);

    if (!isEqual(payloadRef.current, payload)) {
        paginationRef.current.pageNum = 1;
        payloadRef.current = payload;
    }

    let targetUrl = url;
    let targetPayload: any = payload;
    if (paginated) {
        switch (type) {
            case 'post':
                if (pageInBody) {
                    targetPayload = { ...payload, ...paginationRef.current };
                } else {
                    targetUrl = `${url}?${object2Query(paginationRef.current)}`;
                }
                break;
            case 'get':
                targetPayload = { ...payload, ...paginationRef.current };
                break;
            default:
                break;
        }
    }

    if (isEmpty(targetPayload)) {
        targetPayload = undefined;
    }

    const key = useKey(targetUrl, targetPayload ?? {});

    const dataCatche = useRef({});

    const [returnData, setReturnData] = useState(dataCatche.current[key] || defaultState);
    const [loading, setLoading] = useState<boolean>(false);
    const rendered = useState<any>(null)[1];

    const revalidate = useCallback(() => {
        let isValidating = loading;

        function changeLoading(value) {
            if (useValidating) {
                if (isValidating !== value) {
                    isValidating = value;
                    setLoading(value);
                }
            }
        }

        function sent(res) {
            if (cacheDurationTime !== undefined) {
                cacheMap[key] = {
                    lastFetchTime: Date.now(),
                    response: res
                };
            }
            const response = responseSuccess ? responseSuccess(res) : res;
            const data = get(response, 'data');
            let dataJson: any = {};
            let dataArray: any[] = [];
            if (paginated) {
                dataArray = getArray(data, 'records');
                dataJson = data;
            } else if (isArray(data)) {
                dataArray = data;
            } else if (isObject(data)) {
                dataJson = data;
            }
            const result = {
                data,
                dataArray,
                dataJson,
                response
            };
            useMergeRender(() => {
                dataCatche.current[key] = result;
                setReturnData(result);
                changeLoading(false);
            });
        }

        function error(error) {
            changeLoading(false);
            Reflect.deleteProperty(dataCatche.current, key);
            Reflect.deleteProperty(cacheMap, key);
            onError?.(error);
        }

        if (apiConfig) {
            useMergeRender(() => {
                if (useCache) {
                    changeLoading(!dataCatche.current[key]);
                    setReturnData(dataCatche.current[key] || defaultState);
                } else {
                    changeLoading(true);
                }
            });
            if (timeout) {
                setTimeout(() => {
                    changeLoading(false);
                    onLoadingSlow?.();
                }, timeout);
            }
            const askerConf: AxiosRequestConfig = {
                ...otherConfig,
                timeout
            };
            if (targetUrl && /^http(s)?:\/\//.test(targetUrl)) {
                askerConf.baseURL = '';
            }
            const cache = cacheMap[key];
            if (cacheDurationTime !== undefined && cache) {
                const { response, lastFetchTime } = cache;
                if (cacheDurationTime === 'infinity') {
                    sent(response);
                    return;
                } else {
                    const timeInterval = Date.now() - lastFetchTime;
                    if (timeInterval <= cacheDurationTime) {
                        sent(response);
                        return;
                    }
                }
            }
            if (type === 'get') {
                axiosServiceApi.get(targetUrl, {
                    ...askerConf,
                    params: targetPayload,
                }).then(sent).catch(error);
            } else {
                axiosServiceApi.post(targetUrl, targetPayload, askerConf).then(sent).catch(error);
            }
        }
    }, [key]);

    useEffect(() => {
        revalidate();
    }, [revalidate]);

    const pagination = useMemo(() => {
        return {
            current: paginationRef.current.pageNum,
            pageSize: paginationRef.current.pageSize,
            total: getNumber(returnData, 'data.total'),
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '500'],
            onChange: (pageNum, pageSize) => {
                paginationRef.current.pageNum = pageNum;
                useMergeRender(() => {
                    onChange && onChange(pageNum, pageSize);
                    rendered({});
                });
            },
            onShowSizeChange: (pageNum, pageSize) => {
                paginationRef.current.pageNum = 1;
                paginationRef.current.pageSize = pageSize;
                useMergeRender(() => {
                    onShowSizeChange && onShowSizeChange(pageNum, pageSize);
                    rendered({});
                });
            },
            ...otherPageProps
        };
    }, [paginationRef.current, returnData, paginationProps]);

    return { isValidating: loading, revalidate, pagination, ...returnData };
};


export interface AsyncReturnType<T = any> {
    data: any;
    dataArray: T[];
    dataJson: T;
    response: any;
    error: any;
};

export const dispatchAsync = <T = any, K = any>(apiConfig: ApiConfig<T, K>): Promise<AsyncReturnType<K>> => {
    const { type, url = '', payload, cacheDurationTime, responseSuccess, ...otherConfig } = apiConfig();

    const axiosConf: AxiosRequestConfig = { ...otherConfig };
    if (url && /^http(s)?:\/\//.test(url)) {
        axiosConf.baseURL = '';
    }
    const key = `${url}?${object2Query(payload ?? {})}`;
    const cache = cacheMap[key];

    function sent(res) {
        if (cacheDurationTime !== undefined) {
            cacheMap[key] = {
                lastFetchTime: Date.now(),
                response: res
            };
        }
        const response = responseSuccess ? responseSuccess(res) : res;
        const data = get(response, 'data');
        let dataJson: any = {};
        let dataArray: any[] = [];
        if (isArray(data)) {
            dataArray = data;
        } else if (isObject(data)) {
            dataJson = data;
        }

        return {
            data,
            dataArray,
            dataJson,
            response,
            error: null
        };
    }

    function error(error) {
        Reflect.deleteProperty(cacheMap, key);
        return {
            error,
            data: {},
            dataArray: [],
            dataJson: {},
            response: {},
        };
    }

    if (cacheDurationTime !== undefined && cache) {
        const { response, lastFetchTime } = cache;
        if (cacheDurationTime === 'infinity') {
            return Promise.resolve(sent(response));
        } else {
            const timeInterval = Date.now() - lastFetchTime;
            if (timeInterval <= cacheDurationTime) {
                return Promise.resolve(sent(response));
            }
        }
    }
    if (type === 'get') {
        return axiosServiceApi.get(url, {
            ...axiosConf,
            params: payload,
        }).then(sent).catch(error) as any;
    } else {
        return axiosServiceApi.post(url, payload, axiosConf).then(sent).catch(error) as any;
    }
};

/**
 * 移除请求缓存
 * @param key 
 */
export function removeAsyncCache(key?: string | RegExp) {
    if (!key) {
        cacheMap = {};
    } else {
        const isStringKey = isString(key);
        const keys = Object.keys(cacheMap);
        for (let i = 0; i < keys.length; i++) {
            const currentKey = keys[i];
            if (isStringKey ? currentKey.includes(key) : key.test(currentKey)) {
                Reflect.deleteProperty(cacheMap, currentKey);
            }
        }
    }
}